import dayjs from "dayjs";
import {
  getLocalTimeDate,
  getUTCDate,
} from "../../../services/manageOrderService";
import { loadoutCartState, Product } from "./types";

let loginId = "";

const authContext = localStorage.getItem("authContext");
if (authContext) {
  loginId = JSON.parse(authContext).user.loginId;
} else {
  console.error("authContext is undefined in localStorage");
}

/**
 * Calculates delivery and invoice creation date ranges based on filter type
 */
const calculateDateRanges = (
  startDate: string | null,
  endDate: string | null,
  filterType: string,
  gapDays: number
) => {
  const adjustDate = (date: string | null, days: number, operation: 'add' | 'subtract') => {
    if (!date) return null;
    return dayjs(date)[operation](days, "day").format("YYYY-MM-DDTHH:mm:ss");
  };

  const isDeliveryFilter = filterType === "deliveryDate";
  const isCreationFilter = filterType === "creationTime";

  return {
    deliveryStartDate: isDeliveryFilter ? startDate : adjustDate(startDate, gapDays, 'add'),
    deliveryEndDate: isDeliveryFilter ? endDate : adjustDate(endDate, gapDays, 'add'),
    invoiceCreationStartDate: isCreationFilter ? startDate : adjustDate(startDate, gapDays, 'subtract'),
    invoiceCreationEndDate: isCreationFilter ? endDate : adjustDate(endDate, gapDays, 'subtract'),
  };
};

export const getValidateRequestBody = async (
  cart: loadoutCartState,
  statusValue: string,
  isStockServiceEnabled: boolean,
  newStatusUpdateConfig: { invoice?: string; order?: string },
  loadNumber: string,
  filterType: string,
  gapBetweenCreationAndDeliveryDate: number
) => {
  const products: Product[] = Object.values(cart?.cartData?.product || {});

  if (products.length === 0) {
    return null;
  }

  const startDate = cart?.cartData?.loadout?.deliveryStartDate?.$d
    ? getLocalTimeDate(
        dayjs(cart?.cartData?.loadout?.deliveryStartDate?.$d),
        "start"
      ).replace(" ", "T")
    : null;

  const endDate = cart?.cartData?.loadout?.deliveryEndDate?.$d
    ? getLocalTimeDate(
        dayjs(cart?.cartData?.loadout?.deliveryEndDate?.$d),
        "end"
      ).replace(" ", "T")
    : null;

  const { 
    deliveryStartDate, 
    deliveryEndDate, 
    invoiceCreationStartDate, 
    invoiceCreationEndDate 
  } = calculateDateRanges(startDate, endDate, filterType, gapBetweenCreationAndDeliveryDate);

  const requestBody: any = {
    vehicleId: cart?.cartData?.loadout?.Vehicle?.vehicleId,
    salesmanId: cart?.cartData?.loadout?.SalesMan?.split(" - ")[0],
    supplier: JSON.parse(localStorage.authContext)?.user?.loginId,
    status: statusValue,
    deliveryStartDate,
    deliveryEndDate,
    invoiceCreationStartDate,
    invoiceCreationEndDate,
    isNew: statusValue === "IN-TRANSIT" && loadNumber ? false : true,
    type: cart?.preseller ? "PRE-SELLER" : "RSU",
    newInvoiceOrOrderStatus:
      cart?.cartData?.salesData?.[0]?.type === "INVOICE"
        ? newStatusUpdateConfig?.invoice || "OUT OF DELIVERY"
        : newStatusUpdateConfig?.order || "OUT OF DELIVERY",
    loadoutSource: cart?.cartData?.salesData?.[0]?.type?.toUpperCase(),
    vehicleCapacity: cart?.cartData?.loadout?.Vehicle?.vehicleCapacity,
    loadOutDetails: cart?.cartData?.salesData?.map((order) => ({
      invoiceNumber:
        order.type === "INVOICE" ? order?.invoiceNumber : order?.orderNumber,
      routeCode: order?.beat || "",
      outletCode: order?.outletCode,
      presellerId: order?.salesRepId,
      status: statusValue,
      totalAmount: order?.billAmount,
      totalCaseQty: order?.totalCaseQuantity,
      totalPieceQty: order?.totalPieceQuantity,
      totalOtherQty: order?.totalOtherUnitQuantity,
      totalCaseLeftQty: 0.0,
      totalPieceLeftQty: 0.0,
      totalOtherLeftQty: 0.0,
      salesInfo: {
        gstAmount: order?.gstAmount,
        tcsAmount: 0.0,
        discountAmount:
          order?.type === "INVOICE"
            ? order?.discountInfo?.find(
                (discount) => discount.discountType === "Summary"
              )?.discount
            : order?.totalDiscountValue,
        finalAmount: order?.netAmount,
        invoiceStatus:
          cart?.cartData?.salesData?.[0]?.type === "INVOICE"
            ? newStatusUpdateConfig?.invoice || "OUT FOR DELIVERY"
            : newStatusUpdateConfig?.order || "OUT FOR DELIVERY",
      },
      loadOutItems:
        order.type === "INVOICE"
          ? order?.salesDetails?.flatMap((item) =>
              isStockServiceEnabled
                ? item?.batchIds.map((batchId: any) => ({
                    skuCode: item?.skuCode,
                    batchCode: item?.batchCode,
                    batchId: batchId?.batchId || "",
                    caseQty: batchId?.caseQty,
                    pieceQty: batchId?.pieceQty,
                    otherQty: batchId?.otherQty,
                    caseQtyLeft: 0.0,
                    pieceQtyLeft: 0.0,
                    otherQtyLeft: 0.0,
                    amount: item?.netAmount,
                    mrp: item?.mrp ?? 0.0,
                    itemType: item?.type === "freeItem" ? "FREE" : "NORMAL",
                  }))
                : {
                    skuCode: item?.skuCode,
                    batchCode: item?.batchCode,
                    batchId: "",
                    caseQty: item?.initialCaseQuantity,
                    pieceQty: item?.initialPieceQuantity,
                    otherQty: item?.initialOtherUnitQuantity,
                    caseQtyLeft: 0.0,
                    pieceQtyLeft: 0.0,
                    otherQtyLeft: 0.0,
                    amount: item?.netAmount,
                    mrp: item?.mrp ?? 0.0,
                    itemType: item?.type === "freeItem" ? "FREE" : "NORMAL",
                  }
            )
          : order?.orderDetails?.map((item) => ({
              skuCode: item?.skuCode,
              batchCode: item?.batchCode,
              batchId: "",
              caseQty: item?.caseQuantity,
              pieceQty: item?.pieceQuantity,
              otherQty: item?.otherUnitQuantity,
              caseQtyLeft: 0.0,
              pieceQtyLeft: 0.0,
              otherQtyLeft: 0.0,
              amount: item?.netAmount,
              mrp: item?.mrp ?? 0.0,
              itemType: item?.type === "freeItem" ? "FREE" : "NORMAL",
            })),
    })),
  };

  if (loadNumber) {
    requestBody.loadNumber = loadNumber;
  }

  return requestBody;
};
